"use client";

import { motion } from "motion/react";
import { useLocale, useTranslations } from "next-intl";
import { useTransition } from "react";

import { usePathname, useRouter } from "@/i18n/navigation";
import { type AppLocale, routing } from "@/i18n/routing";
import { cn } from "@/lib/utils";

// =============================================================================

/**
 * EN / FR pill toggle. Keeps the current page (and its dynamic params).
 */
export function LocaleSwitcher({ className }: { className?: string }) {
  const t = useTranslations("Preferences");
  const locale = useLocale();
  const pathname = usePathname();
  const router = useRouter();
  const [isPending, startTransition] = useTransition();

  const changeLocale = (nextLocale: AppLocale) => {
    if (nextLocale === locale) return;
    startTransition(() => {
      // Same page and filters in the other language
      router.replace(`${pathname}${window.location.search}`, {
        locale: nextLocale,
        scroll: false,
      });
    });
  };

  return (
    <div
      role="group"
      aria-label={t("language")}
      className={cn(
        "relative flex items-center rounded-full border border-foreground/10 p-0.5 text-[0.6875rem] font-semibold tracking-wide",
        isPending && "opacity-60",
        className
      )}
    >
      {routing.locales.map((item) => {
        const isActive = item === locale;
        return (
          <button
            key={item}
            type="button"
            onClick={() => changeLocale(item)}
            aria-pressed={isActive}
            // Starts with the visible code (label in name), then the language
            aria-label={`${item.toUpperCase()}, ${t(`languages.${item}`)}`}
            lang={item}
            className={cn(
              "relative z-10 h-7 w-8 rounded-full uppercase transition-colors",
              isActive
                ? "text-background"
                : "text-foreground/60 hover:text-foreground"
            )}
          >
            {isActive && (
              <motion.span
                layoutId="locale-switcher-pill"
                className="absolute inset-0 -z-10 rounded-full bg-foreground"
                transition={{ type: "spring", stiffness: 500, damping: 35 }}
              />
            )}
            {item}
          </button>
        );
      })}
    </div>
  );
}

// =============================================================================
